import React from "react";

const GUILLOCHE_ANIMATIONS_CSS = `
@keyframes guilloche-spin-center {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
}
@keyframes guilloche-enter {
    from { opacity: 0; transform: scale(0.75); }
    to   { opacity: 1; transform: scale(1); }
}
`;

/**
 * Mirrors `document.visibilityState`. The 240s rotation is a continuous
 * paint cost; pausing it when the tab isn't visible saves the compositor
 * from running the keyframe at 60fps in the background.
 */
function useDocumentVisible(): boolean {
    const [visible, setVisible] = React.useState(
        typeof document === "undefined" ? true : !document.hidden
    );
    React.useEffect(() => {
        const handler = (): void => setVisible(!document.hidden);
        document.addEventListener("visibilitychange", handler);
        return (): void =>
            document.removeEventListener("visibilitychange", handler);
    }, []);
    return visible;
}

interface GuillochePatternProps {
    /** Path to the guilloche image (svg / png). */
    src: string;
    /**
     * Size relative to the container's larger dimension, as a multiplier.
     * Defaults to 3.6 — image renders at 360% of `max(parent width, parent
     * height)`. Driven by CSS container queries (`cqmax`), so the pattern
     * scales automatically with whatever frame it's dropped into.
     *
     * The surrounding parent must be sized (explicit width/height or
     * absolute inset) for the container query to resolve.
     */
    scale?: number;
    /**
     * Explicit width override. Takes precedence over `scale` when provided.
     * Number → pixels. String is passed through (e.g. `"400px"`, `"50vh"`).
     */
    width?: number | string;
    /** Explicit height override. Takes precedence over `scale` when provided. */
    height?: number | string;
    /** Play the fade-scale entrance animation on mount. Defaults to false. */
    animateIn?: boolean;
    /** Extra classes forwarded to the outer wrapper for positioning tweaks. */
    className?: string;
}

function resolveDimension(
    explicit: number | string | undefined,
    fallback: string
): string {
    if (explicit === undefined) return fallback;
    return typeof explicit === "number" ? `${explicit}px` : explicit;
}

/**
 * Rotating guilloche pattern used as a decorative backdrop on landing
 * panels (USDv home, Solomon Standard). Fills its positioned parent via
 * `absolute inset-0` and centers the image inside, then spins the image
 * around its own center at 240s per rotation.
 *
 * By default the image is sized via CSS container queries so it stays
 * `scale`× the container's larger dimension no matter how the frame is
 * laid out. Pass explicit `width` / `height` to opt into fixed sizing.
 *
 * Keyframes (`guilloche-spin-center`, `guilloche-enter`) are injected
 * once per render as a colocated `<style>` tag. Both respect
 * `prefers-reduced-motion` via the `motion-reduce:animate-none` class.
 *
 * @version 1.2.0
 * @published 2026-04-26
 * @history
 *   1.2.0 — Pause the 240s rotation when the tab isn't visible. The
 *           animation otherwise runs in the background at 60fps for the
 *           lifetime of the session.
 *   1.1.0 — Added `scale` prop (default 3) backed by CSS container
 *           queries. `width`/`height` become optional overrides.
 *   1.0.0 — Initial publish with required `width`/`height` props.
 */
export function GuillochePattern({
    src,
    scale = 3.6,
    width,
    height,
    animateIn = false,
    className
}: GuillochePatternProps): React.ReactElement {
    const visible = useDocumentVisible();
    const scaleUnit = `${scale * 100}cqmax`;
    const imgStyle: React.CSSProperties = {
        width: resolveDimension(width, scaleUnit),
        height: resolveDimension(height, scaleUnit),
        animationPlayState: visible ? "running" : "paused"
    };
    // Only establish a size-query container when we actually rely on
    // cqmax — lets explicit pixel sizing escape the cost.
    const wrapperStyle: React.CSSProperties =
        width === undefined && height === undefined
            ? { containerType: "size" }
            : {};
    const wrapperAnim = animateIn
        ? "animate-[guilloche-enter_2000ms_var(--ease-out-cubic)_both] motion-reduce:animate-none"
        : "";
    return (
        <>
            <style
                dangerouslySetInnerHTML={{ __html: GUILLOCHE_ANIMATIONS_CSS }}
            />
            <div
                style={wrapperStyle}
                className={`absolute inset-0 flex items-center justify-center ${wrapperAnim} ${className ?? ""}`}
            >
                <img
                    src={src}
                    alt=""
                    aria-hidden="true"
                    style={imgStyle}
                    className="pointer-events-none max-w-none animate-[guilloche-spin-center_240s_linear_infinite] motion-reduce:animate-none"
                />
            </div>
        </>
    );
}
